import { useState } from 'react';
import { useLang } from 'hooks/lang';

import { Textarea, Button, Column, Row, ShortcutTag } from 'components/Blocks';
import { PencilIcon } from 'components/Icons';
import { FileIconInput } from 'components/Uploads';

import { CommentInputFiles } from './FileInputList';

import { useMultiKeyPress, useFileInput } from 'hooks';

interface CommentInputProps {
  children?: never;
  saveComment: (text: string, files: File[]) => Promise<boolean | void>;
  disabled?: boolean;
  loading?: boolean;
  text?: string;
  close?: () => void;

  allowFiles?: boolean;
}

export function CommentInput({
  saveComment,
  loading,
  disabled,
  text,
  close,
  allowFiles = true,
}: CommentInputProps) {
  const { t } = useLang();

  const [comment, setComment] = useState(text || '');

  const { clearFiles, fileLimitReached, files, onFileInput, onPaste, removeFile } = useFileInput({
    enabled: allowFiles,
  });

  const actuallyDisabled = !comment.trim() || disabled || loading;

  const executeSave = async () => {
    if (actuallyDisabled) return;

    const ok = await saveComment(comment, allowFiles ? files.map((d) => d.file) : []);

    if (!ok) return;

    setComment('');
    clearFiles();
  };

  useMultiKeyPress({
    key: 'Enter',
    triggerKey: 'ctrl',
    action: executeSave,
    isEnabled: !actuallyDisabled,
  });

  return (
    <Column
      className="rounded-md border px-3 py-1 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
      gap="1"
    >
      <Textarea
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        onPaste={onPaste}
        className="border-0 px-0 focus-visible:ring-0"
      />

      <Column gap="2">
        {!!files.length && <CommentInputFiles files={files} removeFile={removeFile} />}

        <Row gap="1">
          {allowFiles && !fileLimitReached && !loading && !disabled && (
            <FileIconInput onChange={onFileInput} />
          )}

          {close && (
            <Button
              disabled={loading}
              variant="ghost"
              type="button"
              color="warning"
              className="ml-auto"
              onClick={close}
            >
              {t('cancel')}
            </Button>
          )}

          <Button
            type="button"
            onClick={executeSave}
            disabled={actuallyDisabled}
            loading={loading}
            variant="ghost"
            className={close ? '' : 'ml-auto'}
            tooltipContent={<ShortcutTag keys={['ctrl', 'Enter']} />}
          >
            <PencilIcon size={14} />
            {t('save')}
          </Button>
        </Row>
      </Column>
    </Column>
  );
}
